import { EVENT, trackEvent } from "@/utils/analytics";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Image from "src/components/image";
import { useNavigationStore } from "src/store/navigationStore";
// import { useStableNavigateCallback } from "@/hooks/useStableNavigateCallback";
import Lottie from "lottie-react";
import dragSliderAnimation from "src/components/lottie/drag_slider.json";

interface NavSliderProps {
  min?: number;
  max?: number;
  step?: number;
  className?: string;
  currentNavMode: string;
}

export default function NavSlider({
  min = 0,
  max = 1,
  step = 0.001,
  className = "",
  currentNavMode,
}: NavSliderProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const progress = useNavigationStore((state) => state.progress);
  const setProgress = useNavigationStore((s) => s.setProgress);
  const setInterrupt = useNavigationStore((s) => s.setInterrupt);
  const setChatAnswer = useNavigationStore((s) => s.setChatAnswer);
  const isInterrupt = useNavigationStore((s) => s.isInterrupt);
  const setIsShowNavigationContinueButton = useNavigationStore(
    (state) => state.setIsShowNavigationContinueButton,
  );
  const setIsShowNavigationRestartButton = useNavigationStore(
    (state) => state.setIsShowNavigationRestartButton,
  );
  // const instantUpdate = useNavigationStore((state) => state.instantUpdate);

  const navigationCheckpoint = useNavigationStore(
    (state) => state.navigationCheckpoint,
  );

  const startAutoPlayDelay = useNavigationStore(
    (state) => state.startAutoPlayDelay,
  );

  const calculatePosition = useNavigationStore(
    (state) => state.calculatePosition,
  );

  // useEffect(() => {
  //   const handleKeyDown = (event) => {
  //     // event.key for modern browsers; ' ' or 'Spacebar' for older
  //     if (event.key === ' ' || event.key === 'Spacebar') {
  //       event.preventDefault();  // optional, if you want to prevent default space scroll
  //       console.log('Space key pressed!@@@@@@');
  //       calculatePosition(0 , 2)
  //     }
  //   };

  //   window.addEventListener('keydown', handleKeyDown);

  //   return () => {
  //     window.removeEventListener('keydown', handleKeyDown);
  //   };
  // }, []);

  const clampValue = (v: number) => Math.min(max, Math.max(min, v));
  const percent = useMemo(() => {
    if (max <= min) return 0;
    return ((progress - min) / (max - min)) * 100;
  }, [progress, min, max]);

  const quantize = (v: number) => {
    const steps = Math.round((v - min) / step);
    return clampValue(min + steps * step);
  };

  const getIconPath = (code: string) => `/assets/icon_dark_${code}.png`;

  const handleValueChange = useCallback(
    (newValue: number) => {
      setProgress(newValue);
      // calculatePosition(newValue, 0);
      // instantUpdate();
      setIsShowNavigationContinueButton(false);
      setIsShowNavigationRestartButton(false);
      startAutoPlayDelay(2000);
    },
    [setProgress, calculatePosition], // calculatePosition is now stable
  );

  useEffect(() => {
    const handleMove = (clientX: number) => {
      const el = trackRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = Math.min(rect.width, Math.max(0, clientX - rect.left));
      const ratio = rect.width > 0 ? x / rect.width : 0;
      const raw = min + ratio * (max - min);
      handleValueChange(quantize(raw));
      trackEvent(EVENT.NAV_PAGE_3D_INTERACTION, {
        mode: "navigation",
        type: "slider",
      });
    };

    const onMouseMove = (e: MouseEvent) => {
      //console.log("onMouseMove: isDragging=", isDragging);
      if (!isDragging) return;
      handleMove(e.clientX);
    };

    const onTouchMove = (e: TouchEvent) => {
      console.log("onTouchMove: isDragging=", isDragging);
      if (!isDragging) return;
      if (e.touches.length > 0) handleMove(e.touches[0].clientX);
    };

    const stop = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", stop);
      window.addEventListener("touchmove", onTouchMove as any, {
        passive: false,
      });
      window.addEventListener("touchend", stop);
    }

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchmove", onTouchMove as any);
      window.removeEventListener("touchend", stop);

      console.log("nav slider cleanup end");
    };
  }, [isDragging, min, max, step, handleValueChange]);

  const startDragMouse = (e: React.MouseEvent) => {
    setIsDragging(true);
    e.preventDefault();
  };
  const startDragTouch = (e: React.TouchEvent) => {
    setIsDragging(true);
    e.preventDefault();
  };
  const jumpTo = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = trackRef.current;
    if (!el) return;
    setChatAnswer("");
    setInterrupt(true);
    const rect = el.getBoundingClientRect();
    const clickX = Math.min(rect.width, Math.max(0, e.clientX - rect.left));
    const ratio = rect.width > 0 ? clickX / rect.width : 0;
    const raw = min + ratio * (max - min);
    handleValueChange(quantize(raw));
  };

  const DragSliderAnimation = () => {
    const isDisplayDragScreenSliderAnimation = useNavigationStore(
      (state) => state.isDisplayDragScreenSliderAnimation,
    );
    return isDisplayDragScreenSliderAnimation ? (
      <div className={`absolute w-[100%] h-[100%] z-[3] pointer-events-none`}>
        <div
          className={`absolute top-[calc(50%+17px)] -translate-y-1/2 left-1/2 -translate-x-1/2 w-[200px] h-[100px]`}
        >
          <Lottie animationData={dragSliderAnimation} loop={true} />
        </div>
      </div>
    ) : (
      <></>
    );
  };

  return (
    <div className={`flex w-full select-none ${className} px-[12px]`}>
      <div
        ref={trackRef}
        className="relative h-4 w-full rounded-full slider-bg"
        onMouseDown={jumpTo}
        onClick={jumpTo}
      >
        {/* start cap */}
        <div
          className={`pointer-events-none absolute left-[5px] top-1/2 -translate-y-1/2 -translate-x-1/2 h-9 w-9 rounded-full slider-cap flex items-center justify-center ${
            percent > 0 ? "slider-filled" : ""
          }`}
        >
          <span className="cap-color h-3 w-3 rounded-full" />
        </div>
        {/* end cap */}
        <div className="pointer-events-none absolute -right-[40px] top-1/2 -translate-y-1/2 -translate-x-1/2 h-9 w-9 rounded-full slider-cap flex items-center justify-center">
          <span className="cap-color h-3 w-3 rounded-full" />
        </div>
        {/* filled track */}
        <div
          className="absolute left-0 top-0 h-[16px] rounded-full slider-filled"
          style={{ width: `${percent}%` }}
        />
        {/* checkpoint dots */}
        {Array.isArray(navigationCheckpoint) &&
          navigationCheckpoint.length > 0 &&
          navigationCheckpoint.map((point, idx) => (
            <div
              key={idx}
              className="pointer-events-none absolute top-1/2 -translate-y-1/2 z-[3]"
              style={{ left: `${point * 100}%` }}
            >
              <div className="cap-color h-3 w-3 rounded-full" />
            </div>
          ))}
        {/* thumb */}
        <button
          className="slider-thumb absolute -top-3 h-9 w-9 -translate-x-1/2 cursor-pointer"
          style={{ left: `${percent}%` }}
          onMouseDown={startDragMouse}
          onTouchStart={startDragTouch}
          role="slider"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={progress}
          aria-label="Progress"
        >
          <div className="flex h-10 w-10 items-center justify-center slider-filled rounded-full backdrop-blur">
            <Image
              aria-hidden
              src={getIconPath(currentNavMode)}
              alt="thumb icon"
              width={20}
              height={20}
            />
          </div>
        </button>
        <DragSliderAnimation />
      </div>
    </div>
  );
}
